/**
 * Pub-sub for missing assets (3D materials, missing translations, etc).
 *
 * Plain `window.dispatchEvent` would drop events fired before the badge component mounts —
 * the badge subscribes in a `useEffect`, but `DomainStore.setDomainData` fires translation
 * events earlier, during initial load. This bus buffers all entries so the badge can pull a
 * snapshot on mount and continue receiving live events afterwards.
 */

export const MISSING_ASSET_EVENT = 'jung:missing-asset';
export const MISSING_ASSET_CLEAR_EVENT = 'jung:missing-asset-clear';
export const MISSING_MATERIAL_PREFIX = '__missing__';

export type MissingAssetDetail = {
  kind: 'material' | 'translation' | 'geometry';
  assetName: string;
  productId: string;
  geometryName: string;
};

const entryKey = (detail: MissingAssetDetail): string => {
  // translation gaps are per-colour-code — the same code appears in every functional group and
  // every combination, so we collapse them to one entry per code. Missing 3D materials, on the
  // other hand, point to a specific (geometry × product) site we want to keep distinguishable.
  if (detail.kind === 'translation') {
    return `translation:${detail.assetName}`;
  }
  return `${detail.kind}:${detail.assetName}:${detail.productId}:${detail.geometryName}`;
};

const buffer = new Map<string, MissingAssetDetail>();

export function notifyMissingAsset(detail: MissingAssetDetail): void {
  const key = entryKey(detail);
  if (buffer.has(key)) {
    return;
  }
  buffer.set(key, detail);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<MissingAssetDetail>(MISSING_ASSET_EVENT, { detail }));
  }
}

export function getMissingAssetsSnapshot(): MissingAssetDetail[] {
  return Array.from(buffer.values());
}

/**
 * Drop all buffered entries — called when switching combination/model so the badge only ever
 * reflects the currently rendered configuration instead of accumulating across everything viewed.
 */
export function clearMissingAssets(): void {
  buffer.clear();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(MISSING_ASSET_CLEAR_EVENT));
  }
}
